/**
 * Variant Image Filter for BTBHub
 *
 * Matches alt-text by `[<SKU>]` prefix (case-insensitive). The full alt-text
 * format on migrated products is `[<SKU>] <Title> - <Type Label>` — see the
 * cross-store image migration in Roxor-Balterley-Shopify/tools/shopify/
 * replace_product_images.js for the canonical format. Images with empty alt
 * (or no `[...]` prefix) are treated as shared/hero and always shown.
 *
 * Sort key still derives from the image source URL filename suffix
 * (`_sp1.`, `_co1.`, `_ls1.`, `_ls2.`, `_ls3.`, `_ld.`) because uploaded
 * filenames are preserved through the Roxor CDN → Shopify CDN pipeline.
 *
 * Legacy-product fallback: if the gallery has zero `[<SKU>]`-prefixed slides,
 * we're on an un-migrated product whose alts are bare SKUs (`BAL101B`, etc.) —
 * use the original exact-match behaviour so the filter still works there
 * during the rolling migration. Without this, every slide would be classified
 * as "shared" and the per-variant filter would no-op on legacy products.
 *
 * Empty-gallery fallback: if (in migrated mode) the active SKU has zero
 * matching slides AND there are no shared (no-prefix) slides, show everything
 * rather than render an empty gallery — better cluttered than blank.
 *
 * Wired to Dawn's pub/sub `variantChange` event for the on-change update,
 * and DOMContentLoaded for the initial paint (using ?variant=<id> URL param
 * or the first available variant).
 */
(function () {
  if (typeof subscribe === 'undefined' || typeof PUB_SUB_EVENTS === 'undefined') return;

  // Two sort orders, picked per-gallery by whether any slide is a silo:
  //   - With silo (premium first asset): silo → lifestyles → cutout → tech drawing
  //     (cutout sits 2nd-to-last because the silo carries the hero)
  //   - No silo (bundles, simple products): cutout → lifestyles → tech drawing
  //     (cutout becomes the hero since there's no silo to take that slot)
  // Mirrors the SORT_KEY/BUNDLE_SORT_KEY policy in the cross-store image
  // migration: Roxor-Balterley-Shopify/tools/shopify/replace_product_images.js
  // Key without trailing dot — UUID-suffixed filenames (Shopify Files collision rename
  // appends `_<uuid>` before the extension) break a literal `_sp1.` lookup, so we
  // extract the bucket via SUFFIX_RE and index by the bare suffix instead.
  var IMAGE_ORDER_WITH_SP1 = { '_sp1': 1, '_ls1': 2, '_ls2': 3, '_ls3': 4, '_co1': 5, '_ld': 6 };
  var IMAGE_ORDER_NO_SP1   = { '_co1': 1, '_ls1': 2, '_ls2': 3, '_ls3': 4, '_sp1': 5, '_ld': 6 };
  var SUFFIX_RE = /(_(?:sp1|co1|ls1|ls2|ls3|ld))(?:_[a-f0-9-]{36})?\./i;
  var SKU_PREFIX_RE = /^\s*\[([^\]]+)\]/;

  function gallerySortMap(slides) {
    // True if any slide's image src has the `_sp1` bucket (with or without UUID suffix).
    var hasSp1 = slides.some(function (slide) {
      var img = slide.querySelector('img');
      var src = img && (img.getAttribute('src') || img.getAttribute('srcset') || '');
      if (!src) return false;
      var m = src.match(SUFFIX_RE);
      return !!(m && m[1].toLowerCase() === '_sp1');
    });
    return hasSp1 ? IMAGE_ORDER_WITH_SP1 : IMAGE_ORDER_NO_SP1;
  }

  function getImageSortKey(imgEl, orderMap) {
    var src = (imgEl.getAttribute('src') || imgEl.getAttribute('srcset') || '').toLowerCase();
    var m = src.match(SUFFIX_RE);
    if (!m) return 99;
    var key = m[1].toLowerCase();
    return orderMap[key] != null ? orderMap[key] : 99;
  }

  function getAltSku(imgEl) {
    var alt = imgEl.getAttribute('alt') || '';
    var m = alt.match(SKU_PREFIX_RE);
    return m ? m[1].trim().toUpperCase() : null;
  }

  function filterGalleryImages(variant) {
    if (!variant) return;
    var skuTag = (variant.sku || '').toUpperCase();
    if (!skuTag) return;

    document.querySelectorAll('media-gallery').forEach(function (gallery) {
      var viewer = gallery.querySelector('[id^="GalleryViewer"]');
      var thumbnails = gallery.querySelector('[id^="GalleryThumbnails"]');
      if (!viewer) return;

      var sliderList = viewer.querySelector('.product__media-list');
      if (!sliderList) return;

      var slides = Array.from(sliderList.querySelectorAll('.product__media-item'));
      var orderMap = gallerySortMap(slides);

      // Detect migrated vs. legacy mode by looking for any `[<SKU>]`-prefixed alt.
      var hasPrefixed = slides.some(function (slide) {
        var img = slide.querySelector('img');
        return img && getAltSku(img) !== null;
      });

      // First pass: count matches + shared (only meaningful in migrated mode).
      var matchCount = 0;
      var sharedCount = 0;
      slides.forEach(function (slide) {
        var img = slide.querySelector('img');
        if (!img) { sharedCount++; return; }
        var altSku = getAltSku(img);
        if (altSku === null) sharedCount++;
        else if (altSku === skuTag) matchCount++;
      });
      var fallbackShowAll = hasPrefixed && matchCount === 0 && sharedCount === 0;

      var visible = [];
      var hidden = [];

      slides.forEach(function (slide) {
        var img = slide.querySelector('img');
        if (!img) { visible.push({ el: slide, order: 99 }); return; }
        var shouldShow;
        if (hasPrefixed) {
          // Migrated / mixed mode: match by `[<SKU>]` prefix; no-prefix = shared.
          var altSku = getAltSku(img);
          shouldShow = fallbackShowAll || altSku === null || altSku === skuTag;
        } else {
          // Legacy mode: original exact-match behaviour (alt === SKU or alt === '').
          var altRaw = (img.getAttribute('alt') || '').toUpperCase();
          shouldShow = altRaw === '' || altRaw === skuTag;
        }
        if (shouldShow) visible.push({ el: slide, order: getImageSortKey(img, orderMap) });
        else hidden.push(slide);
      });

      // Sort visible by image type order
      visible.sort(function (a, b) { return a.order - b.order; });

      // Re-append in correct order. Hidden slides stay in DOM with display:none
      // so any code holding stale references doesn't fall over.
      slides.forEach(function (s) { s.remove(); });

      visible.forEach(function (item, idx) {
        item.el.style.display = '';
        // Dawn's `hide_variants` setting tags variant-linked images (e.g. each
        // variant's _co1) with `product__media-item--variant`. Its CSS hides
        // those unless `is-active`. Our filter has already decided this slide
        // should be visible — strip the class so Dawn's CSS does not re-hide it
        // when our sort puts is-active on a different slide (silo, not cutout).
        item.el.classList.remove('product__media-item--variant');
        item.el.classList.remove('is-active');
        if (idx === 0) item.el.classList.add('is-active');
        sliderList.appendChild(item.el);
      });

      hidden.forEach(function (s) {
        s.style.display = 'none';
        s.classList.remove('is-active');
        sliderList.appendChild(s);
      });

      // Thumbnails — same logic, same sort
      if (thumbnails) {
        var thumbList = thumbnails.querySelector('.thumbnail-list');
        if (thumbList) {
          var thumbItems = Array.from(thumbList.querySelectorAll('.thumbnail-list__item'));
          var visibleThumbs = [];
          var hiddenThumbs = [];

          thumbItems.forEach(function (thumb) {
            var img = thumb.querySelector('img');
            if (!img) { visibleThumbs.push({ el: thumb, order: 99 }); return; }
            var shouldShow;
            if (hasPrefixed) {
              var altSku = getAltSku(img);
              shouldShow = fallbackShowAll || altSku === null || altSku === skuTag;
            } else {
              var altRaw = (img.getAttribute('alt') || '').toUpperCase();
              shouldShow = altRaw === '' || altRaw === skuTag;
            }
            if (shouldShow) visibleThumbs.push({ el: thumb, order: getImageSortKey(img, orderMap) });
            else hiddenThumbs.push(thumb);
          });

          visibleThumbs.sort(function (a, b) { return a.order - b.order; });

          thumbItems.forEach(function (t) { t.remove(); });

          visibleThumbs.forEach(function (item) {
            item.el.style.display = '';
            // Same reasoning as above — strip Dawn's variant-hide class on thumbs.
            // Dawn names this one with a single underscore (`_item--variant`), not
            // `__item--variant`. Confirmed in product-media-gallery.liquid.
            item.el.classList.remove('thumbnail-list_item--variant');
            thumbList.appendChild(item.el);
          });

          hiddenThumbs.forEach(function (t) {
            t.style.display = 'none';
            thumbList.appendChild(t);
          });

          // Make the first visible thumbnail active
          thumbList.querySelectorAll('button').forEach(function (b) { b.removeAttribute('aria-current'); });
          var firstThumbBtn = visibleThumbs.length > 0 ? visibleThumbs[0].el.querySelector('button') : null;
          if (firstThumbBtn) firstThumbBtn.setAttribute('aria-current', 'true');

          // Reset thumbnail scroll
          var thumbSlider = thumbList.closest('slider-component');
          if (thumbSlider && thumbSlider.resetPages) thumbSlider.resetPages();
          thumbList.scrollTo({ left: 0 });
        }
      }

      // Update slider counter
      var counter = gallery.querySelector('.slider-counter--total');
      if (counter) counter.textContent = visible.length;
      var currentCounter = gallery.querySelector('.slider-counter--current');
      if (currentCounter) currentCounter.textContent = visible.length > 0 ? '1' : '0';

      // Reset scroll — guard against empty visible (defensive; shouldn't happen
      // in migrated mode thanks to fallbackShowAll, but a legacy product whose
      // active variant matches no slide AND has no empty-alt slides could).
      if (visible.length > 0) {
        sliderList.scrollTo({ left: 0 });
        if (viewer.resetPages) viewer.resetPages();
      }
    });
  }

  subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
    if (event && event.data && event.data.variant) {
      filterGalleryImages(event.data.variant);
    }
  });

  document.addEventListener('DOMContentLoaded', function () {
    var variantRadios = document.querySelector('variant-radios, variant-selects');
    if (!variantRadios) return;

    var dataEl = variantRadios.querySelector('[type="application/json"]');
    if (!dataEl) return;
    var variantData = JSON.parse(dataEl.textContent);
    if (!variantData || variantData.length === 0) return;

    // Resolve initial variant:
    //   1. ?variant=<id> URL param (deep links)
    //   2. fallback: first available variant (Shopify's default selection)
    var urlParams = new URLSearchParams(window.location.search);
    var variantId = urlParams.get('variant');
    var variant = null;
    if (variantId) {
      variant = variantData.find(function (v) { return v.id == variantId; });
    }
    if (!variant) {
      variant = variantData.find(function (v) { return v.available; }) || variantData[0];
    }
    if (variant) filterGalleryImages(variant);
  });
})();
